import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { paymentApi } from "../../../api/paymentApi";
import { useNotifications } from "../../../context/NotificationContext";
import { toast } from "react-toastify";

import "./PaymentList.css";

const defaultMethods = [
  {
    code: "eft",
    displayName: "Bank transfer (EFT)",
    instructions: "",
    eftDetails: {
      bankName: "",
      accountHolder: "",
      accountNumber: "",
      branchCode: "",
      accountType: "",
      paymentReference: "Use your store request number as the payment reference.",
    },
    enabled: true,
    online: false,
    testMode: false,
    paymentImage: "",
  },
  {
    code: "payfast",
    displayName: "PayFast",
    instructions: "",
    enabled: false,
    online: true,
    testMode: true,
    credentials: {},
    paymentImage: "",
  },
  {
    code: "ozow",
    displayName: "Ozow",
    instructions: "",
    enabled: false,
    online: true,
    testMode: true,
    credentials: {},
    paymentImage: "",
  },
];

const CredentialField = ({
  code,
  label,
  placeholder,
  value,
  visible,
  onChange,
  onToggleVisibility,
}) => (
  <label className="payment-credential-field">
    <span>{label}</span>
    <span className="payment-credential-input">
      <input
        type={visible ? "text" : "password"}
        autoComplete="new-password"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      <button
        type="button"
        className="payment-visibility-toggle"
        aria-label={`${visible ? "Hide" : "Show"} ${label}`}
        aria-pressed={visible}
        onClick={() => onToggleVisibility(code, label)}
      >
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </span>
  </label>
);

const emptyEftDetails = () => ({
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  branchCode: "",
  accountType: "",
  paymentReference: "Use your store request number as the payment reference.",
});

const getEftDetails = (method) => {
  if (method.eftDetails) {
    return { ...emptyEftDetails(), ...method.eftDetails };
  }
  try {
    return { ...emptyEftDetails(), ...JSON.parse(method.instructions || "{}") };
  } catch {
    return emptyEftDetails();
  }
};

const getLegacyEftInstructions = (method) => {
  if (!method.instructions) return "";
  try {
    const parsed = JSON.parse(method.instructions);
    return parsed && typeof parsed === "object" ? "" : method.instructions;
  } catch {
    return method.instructions;
  }
};

const PaymentList = () => {
  const [payments, setPayments] = useState([]);
  const [methods, setMethods] = useState(defaultMethods);
  const [trialDays, setTrialDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState("");
  const [error, setError] = useState("");
  const [visibleCredentials, setVisibleCredentials] = useState({});
  const { socket } = useNotifications();

  const loadPaymentData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [settingsResponse, paymentsResponse] = await Promise.all([
        paymentApi.getAdminSettings(),
        paymentApi.getAdminPayments(),
      ]);
      const savedMethods = (settingsResponse.data?.methods || []).map(
        (method) => ({
          ...method,
          code: String(method.code || "").trim().toLowerCase(),
        }),
      );
      const loadedMethods = ["eft", "payfast", "ozow"].map((code) => {
        const existing = savedMethods.find((method) => method.code === code);
        const loaded = (
          existing ||
          defaultMethods.find((method) => method.code === code)
        );
        return code === "eft"
          ? {
              ...loaded,
              eftDetails: getEftDetails(loaded),
              legacyEftInstructions: getLegacyEftInstructions(loaded),
            }
          : loaded;
      });
      setMethods(
        loadedMethods.map((method) => ({
          ...method,
          credentials: {},
        })),
      );
      setTrialDays(settingsResponse.data?.trialDays || 14);
      setPayments(paymentsResponse.data || []);
    } catch (loadError) {
      setError(
        loadError.response?.data?.error || "Failed to load payment settings.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPaymentData();
    if (!socket) return undefined;
    socket.on("platform:updated", loadPaymentData);
    return () => socket.off("platform:updated", loadPaymentData);
  }, [loadPaymentData, socket]);

  const updateMethod = (code, values) => {
    setMethods((current) =>
      current.map((method) =>
        method.code === code ? { ...method, ...values } : method,
      ),
    );
  };

  const updateCredential = (code, key, value) => {
    setMethods((current) =>
      current.map((method) =>
        method.code === code
          ? {
              ...method,
              credentials: { ...method.credentials, [key]: value },
            }
          : method,
      ),
    );
  };

  const updateEftDetail = (field, value) => {
    setMethods((current) =>
      current.map((method) =>
        method.code === "eft"
          ? {
              ...method,
              eftDetails: { ...emptyEftDetails(), ...method.eftDetails, [field]: value },
            }
          : method,
      ),
    );
  };

  const toggleCredentialVisibility = (code, label) => {
    const key = `${code}:${label}`;
    setVisibleCredentials((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const uploadPaymentImage = (code, file) => {
    if (!file) return;
    if (
      !["image/png", "image/jpeg", "image/webp"].includes(file.type) ||
      file.size > 2 * 1024 * 1024
    ) {
      setError("Choose a PNG, JPG, or WebP image up to 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        setError("Could not read the selected payment image.");
        return;
      }
      setError("");
      updateMethod(code, { paymentImage: reader.result });
    };
    reader.onerror = () => {
      setError("Could not read the selected payment image.");
    };
    reader.readAsDataURL(file);
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      const response = await paymentApi.updateAdminSettings({
        trialDays: Number(trialDays),
        methods,
      });
      setMethods(
        (response.data?.methods || []).map((method) => ({
          ...method,
          credentials: {},
        })),
      );
      setVisibleCredentials({});
      const resetCredentials = response.data?.credentialsReset || [];
      if (resetCredentials.length) {
        toast.warn(
          `${resetCredentials.join(", ")} credentials were replaced. Re-enter the PayFast passphrase if you use one.`,
        );
      } else {
        toast.success("Payment settings saved.");
      }
    } catch (saveError) {
      setError(
        saveError.response?.data?.error || "Failed to save payment settings.",
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmPayment = async (paymentId) => {
    const transactionId = window.prompt(
      "Optional: enter the transaction/reference ID after verifying receipt.",
    );
    if (transactionId === null) return;

    try {
      await paymentApi.confirmPayment(paymentId, transactionId.trim());
      toast.success("Payment confirmed and request approved.");
      await loadPaymentData();
    } catch (confirmError) {
      setError(
        confirmError.response?.data?.error || "Failed to confirm payment.",
      );
    }
  };

  const deletePendingPayment = async (paymentId) => {
    if (
      !window.confirm(
        "Delete this pending payment? This cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setDeletingPaymentId(paymentId);
      setError("");
      await paymentApi.deletePendingPayment(paymentId);
      toast.success("Pending payment deleted.");
      await loadPaymentData();
    } catch (deleteError) {
      setError(
        deleteError.response?.data?.error || "Failed to delete pending payment.",
      );
    } finally {
      setDeletingPaymentId("");
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading payments...</p>
      </div>
    );
  }

  return (
    <div className="admin-payment-list-page">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2>Payments & Trials</h2>
        <span style={{ color: "var(--text-light)" }}>
          Payments: {payments.length}
        </span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      <form className="dashboard-card" onSubmit={saveSettings}>
        <h3>Payment options</h3>
        <p style={{ color: "var(--text-light)" }}>
          Enabled options appear to clients at checkout. Online credentials are
          encrypted before storage.
        </p>
        <label style={{ display: "block", margin: "16px 0" }}>
          Trial duration in days
          <input
            type="number"
            min="1"
            max="365"
            value={trialDays}
            onChange={(event) => setTrialDays(event.target.value)}
            required
            style={{ display: "block", maxWidth: "180px", marginTop: "6px" }}
          />
        </label>

        {methods.map((method) => (
          <section
            key={method.code}
            style={{
              borderTop: "1px solid #e5e7eb",
              padding: "16px 0",
            }}
          >
            <h3 className="payment-method-heading">{method.displayName}</h3>
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <label className="payment-enabled-field">
                <input
                  type="checkbox"
                  checked={method.enabled}
                  onChange={(event) =>
                    updateMethod(method.code, { enabled: event.target.checked })
                  }
                />{" "}
                Enabled for checkout
              </label>
              <label className="payment-display-name-field">
                <span>Payment method name</span>
                <input
                  value={method.displayName}
                  onChange={(event) =>
                    updateMethod(method.code, { displayName: event.target.value })
                  }
                  required
                />
              </label>
              {method.online && (
                <label>
                  <input
                    type="checkbox"
                    checked={method.testMode}
                    onChange={(event) =>
                      updateMethod(method.code, {
                        testMode: event.target.checked,
                      })
                    }
                  />{" "}
                  Test mode
                </label>
              )}
            </div>
            <div className="payment-image-fields">
              <label>
                <span>Payment method image (optional)</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) =>
                    uploadPaymentImage(method.code, event.target.files?.[0])
                  }
                />
              </label>
              <small>
                PNG, JPG, or WebP, up to 2 MB. This image appears in admin
                settings and at client checkout.
              </small>
              {method.paymentImage && (
                <div className="payment-image-preview">
                  <img src={method.paymentImage} alt={`${method.displayName} payment`} />
                  <button
                    type="button"
                    className="payment-image-remove"
                    onClick={() => updateMethod(method.code, { paymentImage: "" })}
                  >
                    Remove image
                  </button>
                </div>
              )}
            </div>
            {method.online && method.code === "payfast" && (
              <div className="payment-credential-fields">
                <h4>PayFast credentials</h4>
                <label>
                  <span>Merchant ID</span>
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder="Enter merchant ID"
                    value={method.credentials?.merchantId || ""}
                    onChange={(event) =>
                      updateCredential(method.code, "merchantId", event.target.value)
                    }
                  />
                </label>
                <CredentialField
                  code={method.code}
                  label="Merchant key"
                  placeholder={
                    method.credentialsConfigured
                      ? "Saved securely — enter a new key to replace it"
                      : "Enter merchant key"
                  }
                  value={method.credentials?.merchantKey || ""}
                  visible={Boolean(visibleCredentials["payfast:Merchant key"])}
                  onChange={(event) =>
                    updateCredential(method.code, "merchantKey", event.target.value)
                  }
                  onToggleVisibility={toggleCredentialVisibility}
                />
                <CredentialField
                  code={method.code}
                  label="Passphrase"
                  placeholder={
                    method.credentialsConfigured
                      ? "Saved securely — leave blank to keep"
                      : "Optional PayFast passphrase"
                  }
                  value={method.credentials?.passphrase || ""}
                  visible={Boolean(visibleCredentials["payfast:Passphrase"])}
                  onChange={(event) =>
                    updateCredential(method.code, "passphrase", event.target.value)
                  }
                  onToggleVisibility={toggleCredentialVisibility}
                />
                {method.credentialsConfigured && (
                  <small className="payment-credentials-status">
                    Credentials are saved encrypted. Secret values are never
                    returned to this page; blank fields keep the saved value.
                  </small>
                )}
              </div>
            )}
            {method.online && method.code === "ozow" && (
              <div className="payment-credential-fields">
                <h4>Ozow credentials</h4>
                <label>
                  <span>Site code</span>
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder="Enter Ozow site code"
                    value={method.credentials?.siteCode || ""}
                    onChange={(event) =>
                      updateCredential(method.code, "siteCode", event.target.value)
                    }
                  />
                </label>
                <CredentialField
                  code={method.code}
                  label="Private key"
                  placeholder={
                    method.credentialsConfigured
                      ? "Saved securely — enter a new key to replace it"
                      : "Enter private key"
                  }
                  value={method.credentials?.privateKey || ""}
                  visible={Boolean(visibleCredentials["ozow:Private key"])}
                  onChange={(event) =>
                    updateCredential(method.code, "privateKey", event.target.value)
                  }
                  onToggleVisibility={toggleCredentialVisibility}
                />
                {method.credentialsConfigured && (
                  <small className="payment-credentials-status">
                    Credentials are saved encrypted. Secret values are never
                    returned to this page; blank fields keep the saved value.
                  </small>
                )}
              </div>
            )}
            {method.code === "eft" && (
              <div className="eft-details-fields">
                {method.legacyEftInstructions && (
                  <div className="eft-legacy-instructions">
                    <strong>Previously saved payment instructions</strong>
                    <p>{method.legacyEftInstructions}</p>
                    <small>
                      Copy any relevant information into the fields below
                      before saving the structured bank details.
                    </small>
                  </div>
                )}
                {[
                  ["bankName", "Bank name"],
                  ["accountHolder", "Account holder"],
                  ["accountNumber", "Account number"],
                  ["branchCode", "Branch code"],
                  ["accountType", "Account type"],
                  ["paymentReference", "Payment reference / instructions"],
                ].map(([field, label]) => (
                  <label key={field}>
                    {label}
                    <input
                      type="text"
                      value={method.eftDetails?.[field] || ""}
                      onChange={(event) =>
                        updateEftDetail(field, event.target.value)
                      }
                      required={method.enabled && field !== "paymentReference"}
                    />
                  </label>
                ))}
              </div>
            )}
            {!method.online && method.code !== "eft" && (
              <textarea
                aria-label={`${method.displayName} instructions`}
                placeholder={
                  method.code === "eft"
                    ? "Bank name, account holder, account number, branch code, account type, and payment reference"
                    : "Client payment instructions"
                }
                value={method.instructions || ""}
                onChange={(event) =>
                  updateMethod(method.code, { instructions: event.target.value })
                }
                rows={3}
                style={{ width: "100%", marginTop: "12px" }}
              />
            )}
            {method.code === "eft" && (
              <p className="payment-instructions-help">
                These saved details appear separately on the client payment
                step when Bank transfer (EFT) is selected.
              </p>
            )}
          </section>
        ))}

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button className="btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      </form>

      <div className="dashboard-card" style={{ overflow: "auto", marginTop: 24 }}>
        <h3>Payment records</h3>
        {payments.length === 0 ? (
          <p style={{ color: "var(--text-light)" }}>No payments found.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                <th style={{ padding: "12px", textAlign: "left" }}>Reference</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Business</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Amount</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Method</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Date</th>
                <th style={{ padding: "12px", textAlign: "left" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.PaymentID}>
                  <td style={{ padding: "12px" }}>{payment.Reference}</td>
                  <td style={{ padding: "12px" }}>
                    {payment.BusinessName}
                    <br />
                    <small>
                      {payment.RequestType === "website"
                        ? `Website request · ${String(payment.WebsiteRequestID).slice(0, 8)}`
                        : payment.RequestNumber}
                    </small>
                  </td>
                  <td style={{ padding: "12px" }}>
                    {payment.Currency} {Number(payment.Amount).toFixed(2)}
                  </td>
                  <td style={{ padding: "12px" }}>{payment.PaymentMethod}</td>
                  <td style={{ padding: "12px" }}>{payment.Status}</td>
                  <td style={{ padding: "12px" }}>
                    {new Date(payment.CreatedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: "12px" }}>
                    {payment.Status === "pending" && (
                      <div className="payment-row-actions">
                        <button
                          type="button"
                          className="btn-success"
                          onClick={() => confirmPayment(payment.PaymentID)}
                        >
                          Confirm payment
                        </button>
                        <button
                          type="button"
                          className="payment-delete-button"
                          onClick={() => deletePendingPayment(payment.PaymentID)}
                          disabled={deletingPaymentId === payment.PaymentID}
                        >
                          {deletingPaymentId === payment.PaymentID
                            ? "Deleting..."
                            : "Delete"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PaymentList;
